import React, { useEffect } from "react";
import { SEO } from "../../components/common/SEO";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { Container } from "@mui/system";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { fetchBlogById } from "../../redux/actions/blogActions";
import he from "he";
import { formatDate } from "../../utils";
import { CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import SignUpSection from "../../components/common/SignUpCTA";
import { Blog } from "../../redux/types/blogTypes";
import ReactRouterLink from "../../components/common/ReactRouterLink";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import StartSellingCTA from "../../components/common/StartSellingCTA";

const BlogView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  return <BlogContainer id={id?.toLowerCase()} navigate={navigate} />;
};

interface BlogContainerProps {
  id?: string;
  navigate: any;
}

const BlogContainer: React.FC<BlogContainerProps> = ({ id, navigate }) => {
  const dispatch = useDispatch();
  const { loading, blogsData } = useSelector((state: RootState) => state.blogs);

  useEffect(() => {
    if (id) {
      dispatch(fetchBlogById(id));
    } else {
      navigate("/blog");
    }
  }, [dispatch, id, navigate]);

  if (!loading && (!blogsData || blogsData.totalRecords === 0)) {
    navigate("/blog");
    return null;
  }

  const blog = blogsData?.blogs[0];

  return (
    <>
      {loading ? (
        <CenteredLoading />
      ) : (
        <RenderBlog
          blog={blog}
          latestBlogURL={blogsData?.latestBlogURL || ""}
          previousBlogURL={blogsData?.previousBlogURL || ""}
        />
      )}
    </>
  );
};

interface RenderBlogProps {
  blog: Blog;
  latestBlogURL: string;
  previousBlogURL: string;
}

const RenderBlog: React.FC<RenderBlogProps> = ({
  blog,
  latestBlogURL,
  previousBlogURL,
}) => {
  return (
    <>
      <SEO
        title={`${blog.blogTitle} • FiddlePiddle • Toys Marketplace`}
        description={blog.blogDescription}
        keywordsContent="Blog, toys marketplace, marketplace for toys, fiddlepiddle"
      />
      <Breadcrumb
        items={[
          { text: "Home", url: "/" },
          { text: "Blog", url: "/blog" },
          { text: blog.blogTitle, url: blog.blogUrl },
        ]}
      />

      <Container maxWidth="md">
        <div style={{ textAlign: "center" }}>
          <span style={{ fontSize: "1.25rem", fontWeight: "bold" }}>
            {blog.blogTitle}
          </span>
          <br />
          <span style={{ fontSize: "smaller", color: "grey" }}>
            Last updated {formatDate(blog.createdTime)}
          </span>
        </div>
        <div
          style={{ margin: "0 auto", maxWidth: "800px" }}
          dangerouslySetInnerHTML={{ __html: he.decode(blog.blogContent) }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <ReactRouterLink
            to={previousBlogURL}
            isLinkUnderline={false}
            linkElement={
              <Button
                variant="outlined"
                color="primary"
                style={{
                  minWidth: 150,
                  margin: "8px",
                  lineHeight: "unset",
                  borderRadius: "20px",
                }}
              >
                Previous Post
              </Button>
            }
          />
          <ReactRouterLink
            to={latestBlogURL}
            isLinkUnderline={false}
            isDisabled={latestBlogURL === blog.blogUrl}
            linkElement={
              <Button
                variant="outlined"
                color="primary"
                style={{
                  minWidth: 150,
                  margin: "8px",
                  lineHeight: "unset",
                  borderRadius: "20px",
                }}
                disabled={latestBlogURL === blog.blogUrl}
              >
                Latest Post
              </Button>
            }
          />
        </div>
        {/* if url is /blog/start-selling-on-fiddlepiddlecom show Start Selling Section else show Sign Up Section */}
        {blog.blogUrl === "/blog/start-selling-on-fiddlepiddlecom" ? (
          <StartSellingCTA />
        ) : (
          <SignUpSection />
        )}
      </Container>
    </>
  );
};

export default BlogView;
